
import {defineComponent, PropType} from "vue";
import {ProductFilterConditionRestDto, ProductFilterRestDto} from "@/api/pharma-cpc-mgmt/models";
import {determineProductFilterConditionType, determineProductFilterConditionTypes} from "@/helpers/functions/catalog";
import ProductList from "@/components/UI/product/ProductList.vue";
import {ProductListSelectionDto} from "@/components/UI/product/ProductListSelectionDto";
import {ProductFilterConditionTypeEnum} from "@/models/enum/ProductFilterConditionTypeEnum";
import {addProductFilter, prepareProductFilterForEditing} from "@/store/pharma-cpc-mgmt/middleware";
import {ProductFilterRestDtoModel} from "@/models/api/pharma-cpc-mgmt/ProductFilterRestDtoModel";
import ProductFilterConditionAll from "@/components/layouts/catalog/product-filter/ProductFilterConditionAll.vue";
import {FormMixin} from "@/components/base/FormMixin";
import ProductFilterConditionProductList
  from "@/components/layouts/catalog/product-filter/ProductFilterConditionProductList.vue";
import BaseSubmittingOverlay from "@/components/UI/Bootstrap/BaseSubmittingOverlay.vue";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import ProductFilterConditionBrandsCategories
  from "@/components/layouts/catalog/product-filter/ProductFilterConditionBrandsCategories.vue";
import ProductFilterConditionTrademarksCategories
  from "@/components/layouts/catalog/product-filter/ProductFilterConditionTrademarksCategories.vue";

export default defineComponent({
  name: "ProductFilter",
  mixins: [FormMixin],
  emits: ['update:modelValue'],
  components: {
    ProductFilterConditionTrademarksCategories,
    ProductFilterConditionBrandsCategories,
    AlertError2,
    BaseSubmittingOverlay,
    ProductFilterConditionProductList, ProductFilterConditionAll, ProductList
  },
  props: {
    modelValue: {
      type: Object as PropType<ProductFilterRestDto>,
      required: true
    },
    isAllProductsFilterAllowed: {
      type: Boolean,
      required: false,
      default: false
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    localProductFilter: ProductFilterRestDtoModel.createWithDefaults() as ProductFilterRestDto
  }),
  mounted() {
    this.reloadContent();
  },
  watch: {
    modelValue() {
      this.syncFromModelValue();
    }
  },
  computed: {
    ProductFilterConditionTypeEnum() {
      return ProductFilterConditionTypeEnum
    },
    productFilterConditionAll(): ProductFilterConditionRestDto | null {
      return this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.ALL) ?? null;
    },
    productFilterConditionBrands(): ProductFilterConditionRestDto | null {
      return this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.BRANDS_CATEGORIES) ?? null;
    },
    productFilterConditionTrademarks(): ProductFilterConditionRestDto | null {
      return this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.TRADEMARKS_CATEGORIES) ?? null;
    },
    productListConditionCnkCodeList(): string[] {
      return this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.CNK_LIST)?.cnk_codes_included ?? [];
    },
    productListConditionProductCodeList(): string[] {
      return this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST)?.product_codes_included ?? [];
    },
    usedFilterConditionTypes(): ProductFilterConditionTypeEnum[] {
      return determineProductFilterConditionTypes(this.localProductFilter);
    },
    globalIncludedProducts(): ProductListSelectionDto {
      return {
        cnkCodes: this.localProductFilter.global_included_cnk_codes || [],
        productCodes: this.localProductFilter.global_included_product_codes || []
      };
    }
  },
  methods: {
    async doReload(): Promise<void> {
      this.syncFromModelValue();
    },
    syncFromModelValue(): void {
      this.localProductFilter = {...this.modelValue};
      prepareProductFilterForEditing(this.localProductFilter);
    },
    handleAddProductFilter(type: ProductFilterConditionTypeEnum): void {
      addProductFilter(this.localProductFilter, type);
      this.emitChangedModel();
    },
    updateGlobalIncludedProducts(value: ProductListSelectionDto) {
      this.localProductFilter.global_included_cnk_codes = value.cnkCodes || [];
      this.localProductFilter.global_included_product_codes = value.productCodes || [];
      this.emitChangedModel();
    },
    updateConditionAll(value: ProductFilterConditionRestDto): void {
      this.updateConditionForType(value, ProductFilterConditionTypeEnum.ALL);
    },
    updateProductListConditionCnkCodes(value: string[]): void {
      const condition: ProductFilterConditionRestDto | undefined = this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.CNK_LIST);
      if (!condition) {
        return;
      }
      condition.cnk_codes_included = value;
      this.emitChangedModel();
    },
    updateProductListConditionProductCodes(value: string[]): void {
      const condition: ProductFilterConditionRestDto | undefined = this.localProductFilter.filter_conditions
        .find((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST);
      if (!condition) {
        return;
      }
      condition.product_codes_included = value;
      this.emitChangedModel();
    },
    updateConditionForType(value: ProductFilterConditionRestDto, type: ProductFilterConditionTypeEnum): void {
      const index: number = this.findIndex(type);
      if (index === -1) {
        return;
      }
      this.localProductFilter.filter_conditions[index] = value;
      this.emitChangedModel();
    },
    async removeProductListConditions(): Promise<void> {
      await this.performGenericDelete(
        async (): Promise<void> => {
          this.localProductFilter.filter_conditions = this.localProductFilter.filter_conditions
            .filter((condition: ProductFilterConditionRestDto) => {
              const type: ProductFilterConditionTypeEnum = determineProductFilterConditionType(condition);
              return ![ProductFilterConditionTypeEnum.CNK_LIST, ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST].includes(type);
            });
        },
        async (): Promise<void> => {
          this.emitChangedModel();
        }
      );
    },
    async removeConditionForType(type: ProductFilterConditionTypeEnum): Promise<void> {
      await this.performGenericDelete(
        async (): Promise<void> => {
          this.localProductFilter.filter_conditions = this.localProductFilter.filter_conditions
            .filter((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) !== type);
        },
        async (): Promise<void> => {
          this.emitChangedModel();
        }
      );
    },
    emitChangedModel(): void {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        filter_conditions: this.localProductFilter.filter_conditions,
        global_exclude_conditions: this.localProductFilter.global_exclude_conditions,
        global_included_cnk_codes: this.localProductFilter.global_included_cnk_codes,
        global_included_product_codes: this.localProductFilter.global_included_product_codes,
      });
    },
    findIndex(type: ProductFilterConditionTypeEnum): number {
      return this.localProductFilter.filter_conditions
        .findIndex((condition: ProductFilterConditionRestDto) => determineProductFilterConditionType(condition) === type);
    }
  }
});
